import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const utc = (iso) => new Date(/Z|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
const t = (iso) => (iso ? utc(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const Sig = ({ src, testId }) => (src ? <img src={src} alt="Firma" className="h-12 w-28 rounded border border-slate-200 bg-white object-contain" data-testid={testId} /> : <span className="text-xs text-amber-700">Firma mancante</span>);
const STATE = { FISSO: "Assegnato fisso", GIORNALIERO: "In uso giornaliero", LIBERO: "Libero" };

const Info = ({ p }) => (
  <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 p-4 text-sm sm:grid-cols-3" data-testid="phone-view-info">
    {[["IMEI telefono", p.imei], ["Numero interno", p.internal_number], ["Marca", p.brand], ["Modello", p.model], ["IMEI SIM", p.sim_imei], ["Numero", p.phone_number], ["Stazione", p.station_name],
      ["Data arrivo", p.arrival_date && new Date(p.arrival_date).toLocaleDateString("it-IT")], ["Stato", STATE[p.state]], ["Assegnatario", p.fixed_assignee || p.daily?.assignee], ["Note", p.notes]].map(([l, v]) => (
      <div key={l} className="min-w-0"><p className="text-xs uppercase tracking-wider text-slate-500">{l}</p><p className="break-words font-medium">{v || "—"}</p></div>
    ))}
    {p.state === "FISSO" && <div className="col-span-2 sm:col-span-3"><p className="mb-1 text-xs uppercase tracking-wider text-slate-500">Firma assegnatario fisso</p><Sig src={p.fixed_signature} testId="phone-view-fixed-sig" /></div>}
  </div>
);

const Row = ({ a }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3" data-testid={`phone-history-row-${a.id}`}>
    <div className="min-w-[9rem] flex-1">
      <p className="font-semibold">{a.assignee}</p>
      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${a.type === "FISSO" ? "border-sky-200 bg-sky-50 text-sky-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>{a.type === "FISSO" ? "Fisso" : "Giornaliero"}</span>
    </div>
    <div className="text-xs"><p className="mb-1 text-slate-500">{a.type === "FISSO" ? "Assegnato" : "Ritiro"} {t(a.pickup_at)}</p><Sig src={a.pickup_signature} testId={`phone-history-pickup-sig-${a.id}`} /></div>
    <div className="text-xs">
      {a.returned_at
        ? <><p className="mb-1 text-slate-500">{a.type === "FISSO" ? "Liberato" : "Riconsegna"} {t(a.returned_at)}</p>{a.type === "GIORNALIERO" && <Sig src={a.return_signature} testId={`phone-history-return-sig-${a.id}`} />}</>
        : <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">{a.type === "FISSO" ? "In corso" : "Da riconsegnare"}</span>}
    </div>
  </div>
);

export const PhoneView = ({ phoneId, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (phoneId) api.get(`/phones/${phoneId}/history`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [phoneId]);
  if (!phoneId) return null;
  const p = data?.phone;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="phone-view-dialog">
        <DialogHeader>
          <DialogTitle>{p ? `${p.brand} ${p.model}`.trim() || "Telefono" : "Caricamento..."}</DialogTitle>
          <DialogDescription>Scheda telefono e storico assegnazioni con firme.</DialogDescription>
        </DialogHeader>
        {p && <>
          <Info p={p} />
          <p className="text-sm font-semibold">Storico assegnazioni ({data.history.length})</p>
          <div className="space-y-2" data-testid="phone-history-list">
            {data.history.map((a) => <Row key={a.id} a={a} />)}
            {data.history.length === 0 && <p className="py-6 text-center text-sm text-slate-500" data-testid="phone-history-empty">Nessuna assegnazione registrata.</p>}
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
};
